import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { EstimateDocType } from '@/types/estimateDocTemplate';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPatch, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import { useHasPermission } from '@/hooks/usePermissions';

export interface EstimateDocTemplateSettings {
  enabled: boolean;
}

/**
 * The tenant's document-design switch (on/off) for INVOICES — pass
 * `docType: 'INVOICE'`. When off, every invoice PDF stays on the classic invoice
 * layout. Estimates and proposals always render through designs: their family
 * reads a constant `enabled: true` and its write is a no-op.
 */
export function useEstimateDocTemplateSettings(docType?: EstimateDocType) {
  const canView = useHasPermission('company:view');
  const canEdit = useHasPermission('company:edit');
  return useApiQuery<EstimateDocTemplateSettings>({
    queryKey: settingsKeys.estimateDocTemplates(docType),
    queryFn: () =>
      apiGet<EstimateDocTemplateSettings>(
        operationsClient,
        settingsEndpoints.estimateDocTemplates(docType),
      ),
    enabled: canView || canEdit,
  });
}

export function useUpdateEstimateDocTemplateSettings(docType?: EstimateDocType) {
  const queryClient = useQueryClient();
  return useApiMutation<EstimateDocTemplateSettings, Partial<EstimateDocTemplateSettings>>({
    mutationFn: (dto) =>
      apiPatch<EstimateDocTemplateSettings, Partial<EstimateDocTemplateSettings>>(
        operationsClient,
        settingsEndpoints.updateEstimateDocTemplates(docType),
        dto,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.estimateDocTemplates(docType), data);
      // Flipping the invoice switch changes whether EVERY invoice resolves a
      // block layout or the classic one, so drop the cached
      // per-document document-layout queries to force a re-resolve.
      void queryClient.invalidateQueries({
        predicate: (q) => q.queryKey.includes('document-layout'),
      });
      toast.success(data.enabled ? 'Document designs enabled' : 'Document designs disabled');
    },
  });
}
